import { useCallback, useEffect, useMemo, useRef, useState } from "react";

import {
    deleteNetworkEntity as deleteEntityApi,
    getNetworkEntities,
} from "../../../../Api/NetworkEntityApi";
import CODE from "../../../../Static/Constants/StatusCodes";

const DEFAULT_LIMIT = 25;

const INITIAL_QUERY = {
    // Bumped to force a refetch when nothing else about the query changed --
    // deleting the last row of page 1, for instance. Never sent to the server.
    refresh: 0,
    // The entity type lives IN the query, not beside it. Held separately it
    // changed in one commit and the reset landed in the next, so a tab switch
    // fired a request for the new type carrying the old page, search column and
    // filters -- then a second, corrected one. See the note below.
    entityTypeCode: null,
    page: 1,
    limit: DEFAULT_LIMIT,
    searchTerm: "",
    searchBy: null,
    sortBy: null,
    sortOrder: null,
    // Panel selection, already mapped to request params. Kept inside `query`
    // like every other narrowing input so a filter change and its page reset
    // are one state update, not two renders and two requests.
    filterParams: {},
};

// List state for one entity type: fetch, paginate, search, sort, and refetch
// after a write. One instance per visible tab.
//
// EVERY narrowing input -- the entity type included -- lives in ONE state
// object, and each change writes the whole new shape in a single update. Split
// across two pieces of state, a change to one is committed in a different
// render than the reset of the other, and each commit fires a request: the
// first for the new entity type with the previous tab's page, search column and
// filters, the second with those cleared. That is the shape fixed in 820779fc.
//
// The reset is done DURING RENDER rather than in an effect, which is React's
// documented way to adjust state when a prop changes: the component re-renders
// with the corrected state before anything commits, so the effect below never
// sees the stale combination at all. A skip-first-run ref is the wrong fix and
// always has been.
export const useNetworkEntities = (entityTypeCode) => {
    const [records, setRecords] = useState([]);
    const [total, setTotal] = useState(0);
    const [isLoading, setIsLoading] = useState(false);
    const [errorMessage, setErrorMessage] = useState("");
    const [query, setQuery] = useState({ ...INITIAL_QUERY, entityTypeCode });

    // Adjust-state-on-prop-change, not an effect. Bails when nothing moved, so
    // this is not a render loop.
    if (query.entityTypeCode !== entityTypeCode) {
        setQuery({ ...INITIAL_QUERY, entityTypeCode });
    }

    // Guards against a slow earlier response overwriting a faster later one --
    // easy to hit by typing, where the first keystroke can land after the third.
    const requestSeq = useRef(0);

    // The search/sort/filter half of the request, shared verbatim with the
    // export so the downloaded file matches the rows on screen.
    const requestArgs = useMemo(() => ({
        entityTypeCode: query.entityTypeCode,
        search: query.searchTerm || undefined,
        searchBy: query.searchBy || undefined,
        sortBy: query.sortBy || undefined,
        sortOrder: query.sortOrder || undefined,
        ...query.filterParams,
    }), [query]);

    const fetchList = useCallback(async () => {
        if (!query.entityTypeCode) return;
        const seq = ++requestSeq.current;
        setIsLoading(true);
        setErrorMessage("");

        const res = await getNetworkEntities({
            ...requestArgs,
            page: query.page,
            limit: query.limit,
        });

        // Superseded by a later request: leave every piece of state, the
        // loading flag included, to the winner. The winner always holds the
        // highest seq, so it always reaches the setIsLoading(false) below --
        // clearing the flag here instead would let a slow loser turn the
        // spinner off while the request the user is waiting on is still out.
        if (seq !== requestSeq.current) return;

        if (res?.responseStatus?.code === CODE.SUCCESS) {
            setRecords(res?.data?.records || []);
            setTotal(res?.data?.pagination?.total || 0);
        } else if (res?.responseStatus?.code === CODE.SESSION_EXPIRED) {
            setRecords([]);
            setTotal(0);
            setErrorMessage("Your session has expired. Please sign in again.");
        } else {
            setRecords([]);
            setTotal(0);
            setErrorMessage(res?.responseStatus?.msg || "Could not load the list");
        }
        setIsLoading(false);
    }, [query, requestArgs]);

    useEffect(() => {
        fetchList();
    }, [fetchList]);

    // Switching tabs clears the rows: keeping them showed the previous type's
    // data under the new type's columns until the response landed. The query
    // itself is reset during render above, so this only drops the stale rows.
    useEffect(() => {
        setRecords([]);
        setTotal(0);
    }, [entityTypeCode]);

    const setPage = useCallback((page) => {
        setQuery((prev) => (prev.page === page ? prev : { ...prev, page }));
    }, []);

    const setLimit = useCallback((limit) => {
        setQuery((prev) =>
            prev.limit === limit ? prev : { ...prev, limit, page: 1 }
        );
    }, []);

    // page:1 in the same update as the search -- not a follow-up effect.
    // Bails when nothing changed, so re-submitting the same term is not a
    // second request.
    const applySearch = useCallback((term, by) => {
        setQuery((prev) => {
            const searchTerm = term || "";
            const searchBy = by || null;
            if (prev.searchTerm === searchTerm && prev.searchBy === searchBy) {
                return prev;
            }
            return { ...prev, searchTerm, searchBy, page: 1 };
        });
    }, []);

    // Same single-update rule as applySearch: the panel hands back its whole
    // selection, so a shallow compare of the mapped params is what decides
    // whether anything actually changed.
    const applyFilterParams = useCallback((filterParams) => {
        setQuery((prev) => {
            const next = filterParams || {};
            const prevKeys = Object.keys(prev.filterParams);
            const nextKeys = Object.keys(next);
            const unchanged =
                prevKeys.length === nextKeys.length &&
                nextKeys.every((k) => String(prev.filterParams[k]) === String(next[k]));
            return unchanged ? prev : { ...prev, filterParams: next, page: 1 };
        });
    }, []);

    const applySort = useCallback((by, order) => {
        setQuery((prev) => {
            const sortBy = by || null;
            const sortOrder = order || null;
            if (prev.sortBy === sortBy && prev.sortOrder === sortOrder) {
                return prev;
            }
            return { ...prev, sortBy, sortOrder, page: 1 };
        });
    }, []);

    const removeEntity = useCallback(async (entityID) => {
        const res = await deleteEntityApi(entityID);
        const ok = res?.responseStatus?.code === CODE.SUCCESS;
        if (ok) {
            // Refetch rather than splicing locally: the row may have been the
            // last on its page, and totals and pagination move with it. Step
            // back a page when it was the only one left.
            //
            // The state change IS the refetch -- calling fetchList() as well
            // would send a request built from the pre-update query, i.e. the
            // page that just emptied, followed by the corrected one. `refresh`
            // exists so that deleting from page 1, where the page number does
            // not move, still changes the query and so still refetches.
            setQuery((prev) => ({
                ...prev,
                page: records.length === 1 && prev.page > 1
                    ? prev.page - 1
                    : prev.page,
                refresh: (prev.refresh || 0) + 1,
            }));
        }
        return { ok, message: res?.responseStatus?.msg };
    }, [records.length]);

    return {
        records,
        total,
        isLoading,
        errorMessage,
        page: query.page,
        limit: query.limit,
        setPage,
        setLimit,
        applySearch,
        applySort,
        applyFilterParams,
        requestArgs,
        refetch: fetchList,
        removeEntity,
    };
};

export default useNetworkEntities;
